import { Icon } from "../../atoms/icons";
import { Typography } from "../../atoms/typography";
import { cn, platformProps } from "../../lib";
import { webRole } from "../../lib/web-role";
import { Box } from "../../primitives";
import { iconSize } from "../../tokens";
import {
  bannerContainerClasses,
  bannerDescriptionColor,
  bannerIconClassName,
  bannerIconName,
  bannerMessageColor,
  bannerTitleColor,
} from "./lib";
import type { BannerProps } from "./types";

export const Banner = ({
  title,
  description,
  variant = "default",
  className,
  testID,
  accessibilityLabel,
  showIcon = true,
}: BannerProps) => {
  const label = accessibilityLabel || undefined;
  // Without a title it's Figma's Title=False: the message alone, centred
  // beside the icon, in tighter padding
  const hasTitle = Boolean(title);
  return (
    <Box
      {...platformProps({ testID, slot: "banner" })}
      // Only a danger Banner interrupts as an alert; the others are read in
      // place. A caller's label names the Banner, which on web needs a group
      accessibilityRole={variant === "danger" ? "alert" : label ? webRole("group") : undefined}
      accessibilityLabel={label}
      className={cn(
        "gap-spacing-sm rounded-radius-lg md:rounded-radius-xl border-1 flex flex-row",
        hasTitle ? "p-spacing-md items-start" : "p-spacing-sm items-center",
        bannerContainerClasses[variant],
        className,
      )}
    >
      {showIcon && (
        <Icon
          name={bannerIconName[variant]}
          weight="fill"
          size={iconSize.md}
          className={cn("shrink-0", bannerIconClassName[variant])}
        />
      )}

      {/* The text can narrow below its longest word, so an unbroken string
        (a reference, a link) wraps instead of squeezing out the icon or
        running past the edge */}
      <Box className="gap-spacing-xs flex min-w-0 flex-1 flex-col">
        {hasTitle ? (
          <Typography
            variant="body-sm"
            color={bannerTitleColor[variant]}
            className="web:break-words"
          >
            {title}
          </Typography>
        ) : null}
        {description ? (
          <Typography
            variant={hasTitle ? "caption-md" : "body-sm"}
            color={hasTitle ? bannerDescriptionColor[variant] : bannerMessageColor[variant]}
            className="web:break-words"
          >
            {description}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

Banner.displayName = "Banner";
